import React, {useEffect, useState, useCallback} from 'react';
import styles from './CloudPaymentsCheckout.module.scss';
import {copyToClipboard, useNotifications} from '../../lib';

interface ICloudPaymentsCheckout {
  createCryptogramPacket: () => {
    success: boolean;
    packet?: string;
    messages?: Record<string, string>;
  };
}

declare global {
  interface Window {
    cp?: {
      Checkout: new (publicId: string, form: HTMLFormElement) => ICloudPaymentsCheckout;
    };
  }
}

export const CloudPaymentsCheckout = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checkout, setCheckout] = useState<ICloudPaymentsCheckout | null>(null);
  const [cryptogram, setCryptogram] = useState<string | null>(null);
  const {createNotification} = useNotifications();

  // Загрузка скрипта CloudPayments Checkout
  useEffect(() => {
    // Проверяем, не загружен ли уже скрипт
    if (window.cp) {
      setIsScriptLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://checkout.cloudpayments.ru/checkout.js';
    script.async = true;

    script.onload = () => {
      setIsScriptLoaded(true);
      setError(null);
    };

    script.onerror = () => {
      setError('Ошибка загрузки скрипта CloudPayments Checkout');
      setIsScriptLoaded(false);
    };

    document.head.appendChild(script);

    return () => {};
  }, []);

  const publicId = 'pk_05f54c6e7b92763e80c0725ba2d5b';

  // Инициализация checkout при загрузке скрипта
  useEffect(() => {
    if (isScriptLoaded && window.cp && !checkout) {
      const form = document.getElementById('payment-form') as HTMLFormElement;
      if (form) {
        try {
          const checkoutInstance = new window.cp.Checkout(publicId, form);
          setCheckout(checkoutInstance);
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Ошибка инициализации checkout';
          setError(errorMessage);
        }
      }
    }
  }, [isScriptLoaded, publicId, checkout]);

  const handleCreateCryptogram = useCallback(async () => {
    if (!checkout) {
      setError('Checkout не инициализирован');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = checkout.createCryptogramPacket();

      if (result.success && result.packet) {
        setCryptogram(result.packet);
        setError(null);

        const copySuccess = await copyToClipboard(result.packet);

        if (copySuccess) {
          createNotification({
            name: 'cryptogram-copied',
            title: 'Криптограмма скопирована',
            theme: 'success',
          });
        } else {
          createNotification({
            name: 'cryptogram-copy-error',
            title: 'Ошибка копирования',
            theme: 'warning',
          });
        }
      } else if (result.messages) {
        setError('Ошибки в форме');
        setCryptogram(null);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Ошибка создания криптограммы';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [checkout, createNotification]);

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <h2 className={styles.title}>CloudPayments Checkout</h2>

        <form id="payment-form" autoComplete="off" className={styles.form}>
          <div className={styles.formGroup}>
            <label htmlFor="cardNumber" className={styles.label}>
              Номер карты
            </label>
            <input
              type="text"
              data-cp="cardNumber"
              id="cardNumber"
              className={styles.input}
              placeholder="1234 5678 9012 3456"
              defaultValue="4242 4242 4242 4242"
              maxLength={19}
            />
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="expDateMonth" className={styles.label}>
                Месяц
              </label>
              <input
                type="text"
                data-cp="expDateMonth"
                id="expDateMonth"
                className={styles.input}
                placeholder="MM"
                defaultValue="01"
                maxLength={2}
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="expDateYear" className={styles.label}>
                Год
              </label>
              <input
                type="text"
                data-cp="expDateYear"
                id="expDateYear"
                className={styles.input}
                placeholder="YY"
                defaultValue="25"
                maxLength={2}
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="cvv" className={styles.label}>
                CVV
              </label>
              <input
                type="text"
                data-cp="cvv"
                id="cvv"
                className={styles.input}
                placeholder="123"
                defaultValue="777"
                maxLength={4}
              />
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="cardholderName" className={styles.label}>
              Имя держателя карты
            </label>
            <input
              type="text"
              data-cp="name"
              id="cardholderName"
              className={styles.input}
              placeholder="IVAN IVANOV"
              defaultValue="Chad Virgin"
            />
          </div>

          <button
            type="button"
            onClick={handleCreateCryptogram}
            disabled={!isScriptLoaded || !checkout || isLoading}
            className={`${styles.button} ${!isScriptLoaded || !checkout || isLoading ? styles.disabled : ''}`}>
            {isLoading ? 'Обработка...' : 'Создать криптограмму'}
          </button>
          {error && <div className={styles.error}>{error}</div>}

          {!isScriptLoaded && !error && <div className={styles.loading}>Загрузка checkout...</div>}
        </form>
      </div>
      {cryptogram && <div className={styles.cryptogram}>{cryptogram}</div>}
    </div>
  );
};
